import React, { useState } from "react";
import { Avatar, Badge, Button, Dropdown, List, Modal, Tooltip, Typography } from "antd";
import {
  DeleteOutlined,
  ExclamationCircleOutlined,
  MoreOutlined,
  RobotOutlined,
  UserOutlined,
} from "@ant-design/icons";
import styled from "styled-components";

const { Text } = Typography;

const ChatListItemStyled = styled(List.Item)`
  cursor: pointer;
  padding: 12px 16px !important;
  border-bottom: 1px solid #f5f5f5;
  transition: background-color 0.2s ease;
  box-sizing: border-box;
  height: 100%;
  display: flex !important;
  align-items: center;

  .ant-list-item-meta {
    display: flex;
    align-items: center;
    min-width: 0;
    width: 100%;
  }

  .ant-list-item-meta-avatar {
    flex: 0 0 auto;
    margin-right: 16px;
  }

  .ant-list-item-meta-title {
    margin-bottom: 5px !important;
  }

  .ant-list-item-meta-content {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
  }

  ${(props) =>
    props.selected
      ? `
    background-color: #e8f4fd !important;
  `
      : `
    &:hover {
      background-color: #f5f5f5 !important;
    }
  `}

  ${(props) =>
    props.unread
      ? `
    background-color: #f0f9ff;
  `
      : ""}

  ${(props) =>
    props.needHuman
      ? `
    border-left: 3px solid #fa8c16;
  `
      : ""}
`;

function getPreviewText(lastMsg, t) {
  if (!lastMsg) return null;
  const isImage = lastMsg?.messageType === "image";
  const isDocument = lastMsg?.messageType === "document";
  const isAudio = lastMsg?.messageType === "audio";
  if (isImage) return lastMsg?.messageBody || t("chat_image_placeholder");
  if (isDocument) return lastMsg?.messageBody || t("chat_document_placeholder");
  if (isAudio) return lastMsg?.messageBody || t("chat_audio_placeholder");
  return lastMsg?.messageBody;
}

const ChatListRow = React.memo(function ChatListRow({ chat, selected, formatChatListTime, t, onSelect, onDelete }) {
  const [hovered, setHovered] = useState(false);

  const menuItems = [
    {
      key: "delete",
      label: t("delete_chat"),
      icon: <DeleteOutlined />,
      danger: true,
      onClick: () => {
        Modal.confirm({
          title: t("delete_chat_confirm_title"),
          content: t("delete_chat_confirm_description"),
          okText: t("yes"),
          cancelText: t("no"),
          okButtonProps: { danger: true },
          onOk: () => onDelete(chat),
        });
      },
    },
  ];

  const previewText = getPreviewText(chat.lastMessage, t);

  return (
    <ChatListItemStyled
      onClick={() => onSelect(chat)}
      selected={selected}
      unread={chat.unreadMessageCount > 0}
      needHuman={chat.needHuman === true}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <List.Item.Meta
        avatar={
          <Badge count={chat.unreadMessageCount} size="small">
            <Avatar
              src={chat.avatar}
              icon={<UserOutlined />}
              style={{ backgroundColor: chat.unreadMessageCount > 0 ? "#52c41a" : "#1890ff" }}
            />
          </Badge>
        }
        title={
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "8px" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                flex: 1,
                minWidth: 0,
              }}
            >
              <Text strong={chat.unreadMessageCount > 0} style={{ fontSize: "16px" }} ellipsis>
                {chat.contactName}
              </Text>
              {chat.needHuman === true && (
                <Tooltip title={chat.needHumanReason || t("needs_human_review")}>
                  <ExclamationCircleOutlined style={{ color: "#fa8c16", fontSize: "16px" }} />
                </Tooltip>
              )}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              {hovered && (
                <Dropdown menu={{ items: menuItems }} trigger={["click"]} placement="bottomRight">
                  <Button
                    type="text"
                    icon={<MoreOutlined />}
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    style={{ padding: "4px" }}
                  />
                </Dropdown>
              )}
              {chat.mode === "auto" && <RobotOutlined style={{ color: "#880e4f", fontSize: "14px" }} />}
              <Text type="secondary" style={{ fontSize: "12px", whiteSpace: "nowrap" }}>
                {formatChatListTime(chat.updatedAt)}
              </Text>
            </div>
          </div>
        }
        description={
          previewText != null ? (
            <div>
              <Text
                ellipsis
                style={{
                  fontSize: "14px",
                  fontWeight: chat.unreadMessageCount > 0 ? 600 : 400,
                  color: chat.unreadMessageCount > 0 ? "#333" : "#667781",
                  display: "block",
                  marginTop: "2px",
                }}
              >
                {previewText}
              </Text>
            </div>
          ) : null
        }
      />
    </ChatListItemStyled>
  );
});

export default ChatListRow;
